import React, { useMemo } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface EquityCurveProps {
  points: Array<{
    timestamp: string;
    cumulative_pnl: number;
    realized_pnl: number;
    symbol: string;
  }>;
  height?: number;
}

const formatPnl = (value: number): string => {
  const sign = value >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const EquityCurve: React.FC<EquityCurveProps> = ({ points, height = 200 }) => {
  const chartData = useMemo(() => {
    if (points.length === 0) return null;

    const pnlValues = points.map((p) => p.cumulative_pnl);
    const minPnl = Math.min(...pnlValues, 0);
    const maxPnl = Math.max(...pnlValues, 0);
    const range = maxPnl - minPnl || 1;

    const padding = { top: 20, right: 20, bottom: 30, left: 10 };
    const viewWidth = 800;
    const viewHeight = height;
    const plotWidth = viewWidth - padding.left - padding.right;
    const plotHeight = viewHeight - padding.top - padding.bottom;

    const zeroY = padding.top + ((maxPnl - 0) / range) * plotHeight;
    const zeroPct = (maxPnl - 0) / range;

    const coords = points.map((p, i) => {
      const x = padding.left + (i / Math.max(points.length - 1, 1)) * plotWidth;
      const y = padding.top + ((maxPnl - p.cumulative_pnl) / range) * plotHeight;
      return { x, y, point: p };
    });

    const linePoints = coords.map((c) => `${c.x},${c.y}`).join(' ');

    const firstCoord = coords[0];
    const lastCoord = coords[coords.length - 1];
    const fillPoints = `${firstCoord.x},${padding.top + plotHeight} ${linePoints} ${lastCoord.x},${padding.top + plotHeight}`;

    return {
      coords,
      linePoints,
      fillPoints,
      zeroY,
      zeroPct,
      viewWidth,
      viewHeight,
      padding,
      plotWidth,
      plotHeight,
      finalPnl: points[points.length - 1].cumulative_pnl,
      startDate: points[0].timestamp,
      endDate: points[points.length - 1].timestamp,
    };
  }, [points, height]);

  if (!chartData) {
    return (
      <div
        className="flex items-center justify-center text-muted-foreground"
        style={{ height }}
      >
        <p className="text-sm">No data</p>
      </div>
    );
  }

  const {
    coords,
    linePoints,
    fillPoints,
    zeroY,
    zeroPct,
    viewWidth,
    viewHeight,
    padding,
    plotWidth,
    finalPnl,
    startDate,
    endDate,
  } = chartData;

  const formatDateLabel = (ts: string): string => {
    try {
      const d = new Date(ts);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return ts;
    }
  };

  const lineColor = finalPnl >= 0 ? '#10b981' : '#ef4444';
  const textSecondaryColor = '#a1a1aa';

  return (
    <div className="relative w-full">
      <span
        className={`absolute top-1 right-1 z-10 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold text-white ${
          finalPnl >= 0 ? 'bg-emerald-500' : 'bg-rose-500'
        }`}
      >
        {formatPnl(finalPnl)}
      </span>
      <TooltipProvider>
        <svg
          width="100%"
          viewBox={`0 0 ${viewWidth} ${viewHeight}`}
          preserveAspectRatio="none"
          style={{ display: 'block' }}
        >
          <defs>
            <linearGradient id="equityFillGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
              <stop offset={`${zeroPct * 100}%`} stopColor="#10b981" stopOpacity={0.05} />
              <stop offset={`${zeroPct * 100}%`} stopColor="#ef4444" stopOpacity={0.05} />
              <stop offset="100%" stopColor="#ef4444" stopOpacity={0.4} />
            </linearGradient>
          </defs>

          {/* Fill area */}
          <polygon points={fillPoints} fill="url(#equityFillGradient)" />

          {/* Zero line */}
          <line
            x1={padding.left}
            y1={zeroY}
            x2={padding.left + plotWidth}
            y2={zeroY}
            stroke={textSecondaryColor}
            strokeWidth={1}
            strokeDasharray="6,4"
            opacity={0.5}
          />

          {/* P&L line */}
          <polyline
            points={linePoints}
            fill="none"
            stroke={lineColor}
            strokeWidth={2}
          />

          {/* Hover points */}
          {coords.map((c, i) => (
            <Tooltip key={i}>
              <TooltipTrigger asChild>
                <circle cx={c.x} cy={c.y} r={6} fill="transparent" style={{ cursor: 'pointer' }} />
              </TooltipTrigger>
              <TooltipContent>
                <div>
                  <p className="text-xs">
                    {new Date(c.point.timestamp).toLocaleString('en-US', { timeZone: 'America/New_York' })}
                  </p>
                  <p className="text-xs">
                    {c.point.symbol}: {formatPnl(c.point.realized_pnl)}
                  </p>
                  <p className="text-xs font-bold">
                    Cumulative: {formatPnl(c.point.cumulative_pnl)}
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          ))}

          {/* Date labels */}
          <text
            x={padding.left}
            y={viewHeight - 4}
            fill={textSecondaryColor}
            fontSize={12}
            textAnchor="start"
          >
            {formatDateLabel(startDate)}
          </text>
          <text
            x={padding.left + plotWidth}
            y={viewHeight - 4}
            fill={textSecondaryColor}
            fontSize={12}
            textAnchor="end"
          >
            {formatDateLabel(endDate)}
          </text>
        </svg>
      </TooltipProvider>
    </div>
  );
};

export default EquityCurve;
